import React, {
  ChangeEventHandler,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Search, ArrowDropDown, ArrowDropUp, Check } from '@mui/icons-material'
import {
  ListItemIcon,
  ListItemText,
  Grow,
  InputAdornment,
  TextField,
  ClickAwayListener,
  Popper,
  List,
} from '@mui/material'

import { filter, toLower } from 'lodash'
import { useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { handleLatinKeysDown } from '../../../utils'
import { FlagIcon } from '../FlagIcon'
import { checkPhone } from './helpers'
import {
  CountryFlagButton,
  SearchFieldFormControl,
  StyledListItem,
  StyledPaper,
} from './StyledComponents'

import { BaseEnumWithKey, countriesArray, MenuListCompositionProps, prefixCode } from './types'

export const MenuListComposition: FC<MenuListCompositionProps> = ({
  countryCode,
  error,
  countryKey,
  setCountryKey,
  value,
  name,
  componentDisabled = false,
}) => {
  const { t } = useTranslation()
  const { setValue, setError, clearErrors } = useFormContext()

  const [open, setOpen] = useState<boolean>(false)
  // return focus to the button when we transitioned from !open → open
  const prevOpen = React.useRef(open)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [search, setSearch] = useState<string>('')

  const handleToggle = () => {
    setOpen((prevState) => !prevState)
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const clearSearchField = () => {
    if (!open) {
      setSearch('')
    }
  }

  const changeCountry = useCallback(
    (key: string) => {
      setCountryKey(key)
      setOpen(false)
      if (checkPhone(prefixCode(key) + ' ' + value, key)) {
        clearErrors(name)
        setValue(name, prefixCode(key) + ' ' + value, {
          shouldValidate: true,
          shouldDirty: true,
        })
      } else {
        value.length !== 0 &&
          setError(name, {
            type: 'manual',
            message: t('incorrectPhoneNumberFormat', 'Incorrect phone number format'),
          })
      }
    },
    [clearErrors, name, setCountryKey, setError, setValue, t, value],
  )

  const filteredCountries = useMemo(
    () => filter([...countriesArray], (c) => toLower(c.label).includes(toLower(search))),
    [search],
  )

  const handleSearch = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    return setSearch(event.target.value)
  }, [])

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current?.focus()
    }

    prevOpen.current = open
  }, [open])

  return (
    <>
      <CountryFlagButton
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        disabled={componentDisabled}
        sx={{
          boxShadow: error ? 'inset -1px -3px 0px -1px #EF2828' : '',
        }}
        data-test="countryPhoneSelect"
      >
        {countryCode && <FlagIcon code={countryCode} />}
        {open ? <ArrowDropUp /> : <ArrowDropDown />}
      </CountryFlagButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        placement="bottom-start"
        onTransitionEnd={clearSearchField}
        sx={{
          width: '100%',
          zIndex: 2,
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement !== 'bottom-start' ? 'left bottom' : 'left top' }}
          >
            <StyledPaper>
              <ClickAwayListener onClickAway={handleClose}>
                <List id="menu-list-grow">
                  <SearchFieldFormControl>
                    <TextField
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                      onChange={handleSearch}
                      onKeyDown={handleLatinKeysDown}
                      value={search}
                      variant={'standard'}
                      autoFocus
                      placeholder={t('search', 'Search')}
                      data-test="autotest-phoneCountryInput"
                    />
                  </SearchFieldFormControl>
                  {filteredCountries?.map((countryFilter: BaseEnumWithKey) => (
                    <StyledListItem
                      key={countryFilter.value}
                      onClick={() => changeCountry(countryFilter.value)}
                      data-test="autotest-countryPhoneCountry"
                    >
                      <ListItemIcon>
                        <FlagIcon code={toLower(countryFilter.value)} size={'2em'} />
                      </ListItemIcon>
                      <ListItemText>
                        <Trans i18nKey={countryFilter.label} defaults={countryFilter.label} />
                      </ListItemText>
                      {countryKey === countryFilter.value && <Check fontSize={'small'} />}
                    </StyledListItem>
                  ))}
                </List>
              </ClickAwayListener>
            </StyledPaper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
